import React, { useMemo, useState } from 'react';
import { Controller, FieldError } from 'react-hook-form';
import get from 'lodash/get';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { Toggle } from '../../../../CommonComponents/OPL/Toggle/Toggle';
import { IncomeOptionsDTO } from '../../api/useIncomeQuery';
import { AssetBucket } from '../components/AssetBucket/AssetBucket';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';

interface BusinessFormFieldsProps {
  formBag: any;
  fieldNamePrefix?: string;
  options: IncomeOptionsDTO;
  defaultShowAdvanced?: boolean;
}

export const BusinessFormFields = ({
  formBag: { register, control, setValue, watch, formState },
  fieldNamePrefix = '',
  options,
  defaultShowAdvanced = false,
}: BusinessFormFieldsProps) => {
  const [showAdvanced, setShowAdvanced] = useState(defaultShowAdvanced);

  const { BusinessAssetOption } = options;

  const managementStyleValue = watch(`${fieldNamePrefix}OrionManagementStyleId`);

  const startYearOptions = useMemo(
    () => [
      { value: null, label: 'Start Today' },
      ...options.StartYearOptions.map(year => ({
        value: year,
        label: year,
      })),
    ],
    [options.StartYearOptions],
  );

  const endYearOptions = useMemo(
    () => [
      { value: null, label: 'At Retirement' },
      ...options.EndYearOptions.map(year => ({
        value: year,
        label: year,
      })),
    ],
    [options.EndYearOptions],
  );

  return (
    <>
      <Controller
        name={`${fieldNamePrefix}AssociatedAssetId`}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Select
            label="Business Asset"
            value={BusinessAssetOption.find(({ value: v }) => v === value)}
            options={BusinessAssetOption}
            onChange={option => onChange(option ? option.value : null)}
            formVariant={
              get(formState.errors, `${fieldNamePrefix}AssociatedAssetId`) ? 'error' : undefined
            }
            helperText={
              (get(formState.errors, `${fieldNamePrefix}AssociatedAssetId`) as
                | FieldError
                | undefined)?.message
            }
            isPortal
          />
        )}
      />

      <Controller
        control={control}
        name={`${fieldNamePrefix}Amount`}
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            {...field}
            label="Pre-tax Profit"
            decimalScale={2}
            allowNegative={false}
            leftIcon="far fa-dollar-sign"
            innerText="/year"
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            formVariant={get(formState.errors, `${fieldNamePrefix}Amount`) ? 'error' : undefined}
            helperText={
              (get(formState.errors, `${fieldNamePrefix}Amount`) as FieldError | undefined)?.message
            }
          />
        )}
      />

      <ManagementStyle
        control={control}
        options={options}
        fieldName={`${fieldNamePrefix}OrionManagementStyleId`}
        onChange={(value: number | null) => {
          setValue(`${fieldNamePrefix}CustomBucketIndex`, value);
        }}
      />

      <AssetBucket
        control={control}
        fieldName={`${fieldNamePrefix}CustomBucketIndex`}
        managementStyleValue={managementStyleValue}
        options={options}
      />

      <Button
        variant="link"
        leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
        onClick={() => setShowAdvanced(!showAdvanced)}
        aria-expanded={showAdvanced}
        size="small"
        mr="auto"
      >
        {showAdvanced ? 'Hide' : 'Show'} Advanced
      </Button>

      {showAdvanced && (
        <>
          <Controller
            control={control}
            name={`${fieldNamePrefix}IncomeGrowth`}
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Income Growth"
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                tooltip='Enter the income growth rate applied to the Pre-tax Profit field. Leave this field blank to use the global "Income Growth Rate" found in Settings.'
                {...field}
              />
            )}
          />

          <Box
            css={`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px;
            `}
          >
            <Controller
              name={`${fieldNamePrefix}StartAge`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Start Year"
                  value={startYearOptions.find(({ value: v }) => v === value)}
                  options={startYearOptions}
                  onChange={option => onChange(option ? option.value : null)}
                  tooltip="Enter the year when the income will start. To start this income immediately, leave this field blank."
                  isPortal
                />
              )}
            />

            <Controller
              name={`${fieldNamePrefix}EndAge`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="End Year"
                  value={endYearOptions.find(({ value: v }) => v === value)}
                  options={endYearOptions}
                  onChange={option => {
                    onChange(option ? option.value : null);
                    setValue(`${fieldNamePrefix}EndAtRetirement`, option?.value === null);
                  }}
                  tooltip="Enter the year when the income will stop. If left blank, income will stop at sale year of business asset. If both fields left blank, income will stop at retirement."
                  isPortal
                />
              )}
            />
          </Box>

          <Toggle
            {...register(`${fieldNamePrefix}SelfEmployed`)}
            size="small"
            helperText="Applies SECA tax and deducts half the amount from pre-tax profit."
          >
            SECA Tax
          </Toggle>
        </>
      )}
    </>
  );
};
